/**
 * useAnchoredNotesPanel — every piece of anchored-notes state the content
 * review drawer needs, in one hook: the media controller, the CORS/capture
 * verdict, the refreshed native source, the notes session, the resizable
 * column grid, focus mode, and the guarded close / Escape handlers.
 *
 * Split out of ContentReviewPanel.tsx so the component is left with layout.
 * Every hook here is called unconditionally (hook rules); the whole thing is
 * inert unless `notes` is set, exactly as it was inline.
 */
import React from 'react';

import type { ReviewThreadEntry } from '../../../lib/models';
import {
  extractAiFlags,
  isLandscape,
  MIN_COLUMN_PX,
  probeCanvasReadable,
  runEscapeOnce,
  SPLITTER_PX,
  useMediaController,
  useNotesSession,
  useResizableColumns,
  useVideoUrlRefresh,
  UNSAVED_FRAME,
  type ColumnWidths,
  type CorsMode,
  type LoadFailure,
  type MediaController,
  type NotesSession,
  type ResizableColumns,
  type UnsavedWork,
} from './notes';

/** Default CORS attempt — capture needs readable pixels, which needs this. */
const INITIAL_CORS_MODE: CorsMode = 'anonymous';
/** Fallback once the bucket proves it sends no CORS headers. */
const PLAIN_CORS_MODE: CorsMode = 'none';

/**
 * The media itself. The content detail presigns a new URL on every fetch, so
 * the URL string is no identity: the same entry and version is the same video.
 */
/** The canvas-readability check's answer, for the source it checked. */
interface CaptureProbe {
  sourceKey: string;
  readable: boolean;
}

/** The probe answered for this very source, and said its pixels are readable. */
function isReadableSource(probe: CaptureProbe | null, sourceKey: string): boolean {
  return probe?.sourceKey === sourceKey && probe.readable;
}

function sourceIdentity(entryId: string | undefined, versionId: string | undefined): string {
  return `${entryId ?? ''}:${versionId ?? ''}`;
}

/**
 * A source that never reached metadata, even on a fresh URL, may be refusing
 * the anonymous (CORS) load itself — worth one plain load before giving up.
 * One that has loaded before proved its CORS, and one already loaded plainly
 * has had that chance.
 */
function shouldRetryPlain(failure: LoadFailure, plainSourceKey: string | null): boolean {
  return !failure.everLoaded && plainSourceKey !== failure.sourceKey;
}

/** Plain only for the source that proved it refuses the anonymous (CORS) load. */
function corsModeFor(plainSourceKey: string | null, sourceKey: string): CorsMode {
  if (plainSourceKey === sourceKey) return PLAIN_CORS_MODE;
  return INITIAL_CORS_MODE;
}

/** Stable empty thread, so the session's marker memo never sees a fresh `[]`. */
const NO_ENTRIES: ReviewThreadEntry[] = [];

/**
 * The close guard's prompt: whether it's up, and what closing would lose.
 * `unsaved` outlives `open`, so the copy holds still while the dialog fades
 * out; before the first prompt it's a placeholder nothing shows.
 */
export interface DiscardPrompt {
  open: boolean;
  unsaved: UnsavedWork;
}

const NO_DISCARD_PROMPT: DiscardPrompt = { open: false, unsaved: UNSAVED_FRAME };

function closedPrompt(prompt: DiscardPrompt): DiscardPrompt {
  return { ...prompt, open: false };
}

/**
 * Anchored notes mode. When set, the panel mounts the native MediaTheater
 * instead of VideoEmbed, and the shared ChatThread on the right gains the
 * moment props (seekable pills, the tools row, a staged moment). Only pass it
 * when `isNativeReviewable(videoUrl)` is true — every other surface must
 * render exactly as before.
 */
export interface ContentNotesMode {
  entryId: string;
  campaignId?: string;
  versionId: string;
  canWrite: boolean;
  locked: boolean;
  /** Unsent chat draft to seed the chat input with when the player becomes native. */
  initialText?: string;
}

export interface UseAnchoredNotesPanelArgs {
  notes?: ContentNotesMode;
  /** Roster entry id — what the presigned-URL refresh polls. */
  entryId?: string;
  videoUrl?: string;
  /** `aiAnalysis.data` — mined for the scrubber's AI markers. */
  aiData?: Record<string, unknown>;
  /** The chat's entries — their moments become the scrubber's note markers. */
  thread?: ReviewThreadEntry[];
  threadOpen: boolean;
  /** Opens the conversation column — a note marker scrolls the chat, which must be showing. */
  onRevealConversation: () => void;
  onClose: () => void;
}

export interface AnchoredNotesPanel {
  notesEnabled: boolean;
  /** The notes grid — a drag paints its live widths straight onto it. */
  gridRef: React.RefObject<HTMLDivElement | null>;
  controller: MediaController;
  session: NotesSession;
  columns: ResizableColumns;
  corsMode: CorsMode;
  /** The native player's source: the content detail's URL, kept alive by the refresh. */
  nativeSrc: string;
  /** The native video failed for good: the player shows an error with a retry. */
  videoLoadFailed: boolean;
  retryFailedVideo: () => void;
  seekFlashNonce: number;
  focusMode: boolean;
  toggleFocusMode: () => void;
  reviewColumnHidden: boolean;
  gridTemplateColumns: string;
  guardedClose: () => void;
  handleSheetEscape: (event: KeyboardEvent) => void;
  discardPrompt: DiscardPrompt;
  /** Closes the prompt (Cancel, Esc, or once the user confirms); only ever closes it. */
  dismissDiscardPrompt: () => void;
}

interface GridLayout {
  focusMode: boolean;
  threadOpen: boolean;
}

/**
 * `{playerW}px 6px minmax(300px,1fr) 6px {notesW}px`, and in focus mode
 * `minmax(0,1fr) 6px {notesW}px` (the review column is display:none and so
 * claims no track, and its splitter is unmounted). The notes column and its
 * splitter keep their tracks at 0px while the conversation is closed so the
 * toggle still animates.
 */
function gridTemplateFor({ playerW, notesW }: ColumnWidths, { focusMode, threadOpen }: GridLayout): string {
  const notesTrackPx = threadOpen ? notesW : 0;
  const notesSplitterPx = threadOpen ? SPLITTER_PX : 0;
  if (focusMode) return `minmax(0,1fr) ${SPLITTER_PX}px ${notesTrackPx}px`;
  return `${playerW}px ${SPLITTER_PX}px minmax(${MIN_COLUMN_PX}px,1fr) ${notesSplitterPx}px ${notesTrackPx}px`;
}

export function useAnchoredNotesPanel({
  notes,
  entryId,
  videoUrl,
  aiData,
  thread,
  threadOpen,
  onClose,
  onRevealConversation,
}: UseAnchoredNotesPanelArgs): AnchoredNotesPanel {
  const notesEnabled = !!notes;
  const controller = useMediaController();
  const sourceKey = sourceIdentity(entryId, notes?.versionId);

  // Keyed by source, never by URL: a re-presign of the same video must not
  // undo the verdict, and a new entry or version gets its own CORS attempt.
  const [plainSourceKey, setPlainSourceKey] = React.useState<string | null>(null);
  const corsMode = corsModeFor(plainSourceKey, sourceKey);
  // The source whose video failed for good; the player shows an error until a retry.
  const [failedSourceKey, setFailedSourceKey] = React.useState<string | null>(null);

  const [captureProbe, setCaptureProbe] = React.useState<CaptureProbe | null>(null);
  const [seekFlashNonce, bumpSeekFlash] = React.useReducer((n: number) => n + 1, 0);
  const [discardPrompt, setDiscardPrompt] = React.useState<DiscardPrompt>(NO_DISCARD_PROMPT);

  // A failed source first gets a plain reload when that might help (see
  // shouldRetryPlain) — capture stays off then, its pixels unreadable in that
  // mode. Otherwise it has failed for good, and the player says so.
  // The refresh reads this through a latest-ref, so its identity is free to change.
  function onLoadFailed(failure: LoadFailure): void {
    if (shouldRetryPlain(failure, plainSourceKey)) {
      setPlainSourceKey(failure.sourceKey);
      return;
    }
    setFailedSourceKey(failure.sourceKey);
  }

  const { src: nativeSrc, retry: retryVideo } = useVideoUrlRefresh({
    entryId: entryId ?? '',
    sourceKey,
    enabled: notesEnabled && !!entryId,
    controller,
    initialUrl: videoUrl ?? '',
    onLoadFailed,
  });
  const videoLoadFailed = failedSourceKey === sourceKey;
  const retryFailedVideo = React.useCallback(() => {
    setFailedSourceKey(null);
    retryVideo();
  }, [retryVideo]);

  // Capture needs a loaded video fetched with CORS whose pixels the canvas
  // can actually read; only the last check is asynchronous.
  const probeEligible = notesEnabled && controller.ready && corsMode === INITIAL_CORS_MODE;
  React.useEffect(() => {
    const video = controller.videoRef.current;
    if (!probeEligible || !video) return undefined;
    let cancelled = false;
    void probeCanvasReadable(video).then((readable) => {
      if (!cancelled) setCaptureProbe({ sourceKey, readable });
    });
    return () => {
      cancelled = true;
    };
  }, [probeEligible, sourceKey, controller.videoRef]);
  const canCapture = probeEligible && isReadableSource(captureProbe, sourceKey);

  // AI findings become scrubber markers alongside the human notes.
  const aiFlags = React.useMemo(
    () => extractAiFlags(notesEnabled ? aiData?.result : undefined),
    [notesEnabled, aiData],
  );

  // The player side of the review, plus the moment the chat is about to send.
  // Called unconditionally (hook rules); it is inert unless `notes` is set.
  const session = useNotesSession({
    enabled: notesEnabled,
    entryId: notes?.entryId ?? '',
    campaignId: notes?.campaignId,
    versionId: notes?.versionId ?? '',
    canWrite: notes?.canWrite ?? false,
    locked: notes?.locked ?? false,
    controller,
    canCapture,
    onSeekFlash: bumpSeekFlash,
    aiFlags,
    entries: thread ?? NO_ENTRIES,
    onRevealConversation,
    // The close prompt owns the keyboard while it is up.
    suspendShortcuts: discardPrompt.open,
  });

  // Notes-mode layout: two draggable splitters and a focus mode that
  // collapses the AI column. Deliberately notes-only: every other surface
  // keeps its fixed video column and sliding chat column.
  // Not persisted — focus mode is a per-look choice, not a layout preference.
  const [focusMode, setFocusMode] = React.useState(false);
  const gridRef = React.useRef<HTMLDivElement>(null);
  // A drag repaints the grid itself on every move and re-renders the panel only on release.
  function paintLiveWidths(widths: ColumnWidths): void {
    const grid = gridRef.current;
    if (!grid) return;
    grid.style.gridTemplateColumns = gridTemplateFor(widths, { focusMode, threadOpen });
  }
  const landscape = isLandscape(controller.intrinsic);
  const columns = useResizableColumns({ enabled: notesEnabled, landscape, threadOpen, onLiveWidths: paintLiveWidths });
  const toggleFocusMode = React.useCallback(() => setFocusMode((on) => !on), []);
  const reviewColumnHidden = notesEnabled && focusMode;

  // `guardedClose` is bound into the Sheet once; read the latest session
  // through a ref rather than re-subscribing on every render.
  const sessionRef = React.useRef(session);
  // Latest-ref idiom: written in a layout effect, never during render, so a
  // render React throws away can't leave a stale session behind the ref.
  React.useLayoutEffect(() => {
    sessionRef.current = session;
  }, [session]);

  // Closing must never silently drop a captured frame or attached files — the
  // prompt names which.
  const guardedClose = React.useCallback(() => {
    const unsaved = sessionRef.current.unsavedWork();
    if (unsaved === null) {
      onClose();
      return;
    }
    setDiscardPrompt({ open: true, unsaved });
  }, [onClose]);

  const dismissDiscardPrompt = React.useCallback(() => setDiscardPrompt(closedPrompt), []);

  // Radix's SheetContent dismisses on Escape from its own document-level,
  // capture-phase listener (via DismissableLayer), and skips that only when
  // `event.defaultPrevented` is set by the time `onEscapeKeyDown` returns.
  // `useReviewShortcuts` routes the same keypress through the escape stack
  // from a second listener, and which one runs first depends on mount order.
  // `runEscapeOnce` makes the stack run once per keypress either way: when it
  // consumed the key (palette, full view, markup), `preventDefault` here so
  // Radix backs off; otherwise the Sheet closes exactly once via its own
  // `onOpenChange` → `guardedClose`.
  const handleSheetEscape = React.useCallback((event: KeyboardEvent) => {
    if (runEscapeOnce(event, () => sessionRef.current.runEscape())) {
      event.preventDefault();
    }
  }, []);

  const gridTemplateColumns = gridTemplateFor(columns, { focusMode, threadOpen });

  return {
    notesEnabled,
    gridRef,
    controller,
    session,
    columns,
    corsMode,
    nativeSrc,
    videoLoadFailed,
    retryFailedVideo,
    seekFlashNonce,
    focusMode,
    toggleFocusMode,
    reviewColumnHidden,
    gridTemplateColumns,
    guardedClose,
    handleSheetEscape,
    discardPrompt,
    dismissDiscardPrompt,
  };
}
